import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellIcon, CheckIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { PageLoader, Button } from "../../ui";
import VaptSeverityBadge from "../components/VaptSeverityBadge";
import { DataTable } from "../../common/DataTable";
import { useAuth } from "../../../context/AuthContext";
import {
  listVaptNotificationsApi,
  markVaptNotificationReadApi,
  markAllVaptNotificationsReadApi,
  listVaptEnabledCustomersApi,
} from "../../../services/axios/AuthApis/VAPTApis";

const PLATFORM_ADMIN_ROLES = ["platform_admin", "admin"];

// event_type (from vapt/notifications.py) -> UI label + severity.
// severity drives the coloured badge; it is a *display* severity, not the
// finding's own severity (that, when present, comes from metadata.severity).
const EVENT_META = {
  scan_started: { label: "Scan Started", severity: "Informational" },
  scheduled_scan_started: { label: "Scheduled Scan Started", severity: "Informational" },
  scan_completed: { label: "Scan Completed", severity: "Informational" },
  scan_failed: { label: "Scan Failed", severity: "High" },
  scan_blocked: { label: "Scan Blocked", severity: "Medium" },
  critical_finding: { label: "Critical Finding Detected", severity: "Critical" },
  high_finding: { label: "High Finding Detected", severity: "High" },
  remediation_required: { label: "Remediation Required", severity: "Medium" },
  retest_completed: { label: "Retest Completed", severity: "Informational" },
  report_generated: { label: "Report Generated", severity: "Informational" },
  customer_notification: { label: "Customer Notification", severity: "Informational" },
};

const NOTIF_TYPE_SEVERITY = {
  error: "High",
  warning: "Medium",
  success: "Informational",
  info: "Informational",
};

// Older notification rows (created before event_type/metadata were wired)
// have an empty event_type - infer it from reference_id / message so the
// table still shows a useful Type and Severity.
const inferEventType = (n) => {
  if (n.event_type) return n.event_type;
  const ref = (n.reference_id || "").toUpperCase();
  const msg = (n.message || "").toLowerCase();
  if (msg.includes("remediation required")) return "remediation_required";
  if (msg.includes("retest")) return "retest_completed";
  if (msg.includes("report") && msg.includes("generated")) return "report_generated";
  if (msg.includes("blocked")) return "scan_blocked";
  if (msg.includes("scan completed") || msg.includes("no vulnerabilities detected")) return "scan_completed";
  if (msg.includes("scan started")) return "scan_started";
  if (msg.includes("scan") && msg.includes("failed")) return "scan_failed";
  if (msg.includes("critical finding")) return "critical_finding";
  if (msg.includes("high finding")) return "high_finding";
  if (ref.startsWith("SCAN-")) return "scan_completed";
  if (ref.startsWith("FIND-")) return "high_finding";
  if (ref.startsWith("RPT-")) return "report_generated";
  return "";
};

// Route for the "View" action. Prefer the reference_url the backend set;
// otherwise derive a safe VAPT route from the reference_id prefix. Never
// fabricates a route to a specific object that may not exist.
const resolveRoute = (n) => {
  if (n.reference_url) return n.reference_url;
  const ref = n.reference_id || "";
  if (/^SCAN-/i.test(ref)) return `/ai-vapt/scans/${ref}`;
  if (/^FIND-/i.test(ref)) return `/ai-vapt/findings/${ref}`;
  if (/^RPT-/i.test(ref)) return "/ai-vapt/reports";
  const et = inferEventType(n);
  if (et === "remediation_required") return "/ai-vapt/remediation";
  if (et.startsWith("scan")) return "/ai-vapt/scans";
  if (et.endsWith("finding")) return "/ai-vapt/findings";
  if (et === "report_generated") return "/ai-vapt/reports";
  return "/ai-vapt/notifications";
};

const displaySeverity = (n) => {
  const md = n.metadata || {};
  if (md.severity && ["Critical", "High", "Medium", "Low", "Informational"].includes(md.severity)) {
    return md.severity;
  }
  const et = inferEventType(n);
  return EVENT_META[et]?.severity || NOTIF_TYPE_SEVERITY[n.notification_type] || "Informational";
};

const typeLabel = (n) => {
  const et = inferEventType(n);
  return (
    EVENT_META[et]?.label ||
    (n.notification_type ? n.notification_type.replace(/^\w/, (c) => c.toUpperCase()) : "Notification")
  );
};

const VaptNotifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.includes(user?.role);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    listVaptEnabledCustomersApi()
      .then((list) => {
        const rows = list || [];
        setCustomers(rows);
        if (rows.length > 0) setSelectedCustomerId(rows[0].id || rows[0].customer_id || "");
      })
      .catch(() => setCustomers([]));
  }, [isPlatformAdmin]);

  // Non-admins read their own notifications (same id fallback as
  // NotificationContext); a platform_admin reads the SELECTED customer's -
  // the customer's `id` is the customer_admin's UserMaster id, which is the
  // to_user_id VAPT notifications are addressed to. A platform_admin's own
  // id is never used as a recipient id here.
  const recipientId = isPlatformAdmin ? selectedCustomerId : user?.id || user?._id || "";
  const selectedCustomerName = useMemo(
    () => customers.find((c) => (c.id || c.customer_id) === selectedCustomerId)?.name,
    [customers, selectedCustomerId]
  );

  const load = useCallback(() => {
    if (isPlatformAdmin && !selectedCustomerId) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (!recipientId) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    listVaptNotificationsApi(recipientId)
      .then((rows) => setNotifications(Array.isArray(rows) ? rows : []))
      .catch(() => setError("Unable to load VAPT notifications. Please try again."))
      .finally(() => setLoading(false));
  }, [isPlatformAdmin, selectedCustomerId, recipientId]);

  useEffect(() => {
    load();
  }, [load]);

  const openNotification = async (n) => {
    if (!n.is_read) {
      try {
        await markVaptNotificationReadApi(n.id);
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      } catch {
        /* non-fatal - still navigate */
      }
    }
    navigate(resolveRoute(n));
  };

  const handleMarkRead = async (id) => {
    try {
      await markVaptNotificationReadApi(id);
      setNotifications((prev) => prev.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
    } catch {
      setError("Failed to mark notification as read.");
    }
  };

  const handleMarkAllRead = async () => {
    setBusy(true);
    try {
      await markAllVaptNotificationsReadApi(recipientId);
      load();
    } catch {
      setError("Failed to mark all notifications as read.");
    } finally {
      setBusy(false);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const rows = notifications.map((n) => ({ ...n, id: n.id || n._id }));

  const uniqueTypes = [...new Set(rows.map((r) => typeLabel(r)).filter(Boolean))];
  const uniqueSeverities = [...new Set(rows.map((r) => displaySeverity(r)).filter(Boolean))];
  const uniqueProjects = [...new Set(rows.map((r) => r.metadata?.project_id).filter(Boolean))];

  const columns = [
    {
      id: "message",
      accessorFn: (r) => r.message,
      header: "Notification",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Notification", placeholder: "Search message" } },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="flex items-start gap-2 max-w-xl">
            <span
              className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${r.is_read ? "bg-transparent" : "bg-primary-600"}`}
            />
            <div>
              <p className={`text-sm ${r.is_read ? "text-gray-500" : "text-gray-900 font-semibold"}`}>{typeLabel(r)}</p>
              <p className="text-xs text-gray-500">{r.message}</p>
            </div>
          </div>
        );
      },
    },
    {
      id: "type",
      accessorFn: (r) => typeLabel(r),
      header: "Type",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Type",
          placeholder: "All types",
          options: uniqueTypes.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span className="text-xs text-gray-600">{typeLabel(row.original)}</span>,
    },
    {
      id: "severity",
      accessorFn: (r) => displaySeverity(r),
      header: "Severity",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Severity",
          placeholder: "All severities",
          options: uniqueSeverities.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <VaptSeverityBadge severity={displaySeverity(row.original)} size="sm" />,
    },
    {
      id: "project",
      accessorFn: (r) => r.metadata?.project_id || "—",
      header: "Project",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Project",
          placeholder: "All projects",
          options: uniqueProjects.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span className="text-xs text-gray-600">{row.original.metadata?.project_id || "—"}</span>,
    },
    {
      id: "created_at",
      accessorFn: (r) => r.created_at,
      header: "Date",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-xs text-gray-500">
          {row.original.created_at ? new Date(row.original.created_at).toLocaleString() : "—"}
        </span>
      ),
    },
    {
      id: "status",
      accessorFn: (r) => (r.is_read ? "Read" : "Unread"),
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: [
            { value: "Read", label: "Read" },
            { value: "Unread", label: "Unread" },
          ],
        },
      },
      cell: ({ row }) => (
        <span className={`text-xs font-semibold ${row.original.is_read ? "text-gray-400" : "text-primary-600"}`}>
          {row.original.is_read ? "Read" : "Unread"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="flex items-center gap-3">
            {!r.is_read && (
              <button
                type="button"
                onClick={() => handleMarkRead(r.id)}
                className="text-xs font-semibold text-primary-600 hover:text-primary-700 underline"
              >
                Mark read
              </button>
            )}
            <button
              type="button"
              onClick={() => openNotification(r)}
              className="text-xs font-semibold text-gray-500 hover:text-gray-700 underline"
            >
              View
            </button>
          </div>
        );
      },
    },
  ];

  const emptyMessage = isPlatformAdmin
    ? `No VAPT notifications for ${selectedCustomerName || "the selected customer"} yet.`
    : "No VAPT notifications yet. Notifications will appear when VAPT scans, findings, remediation, retests, or reports generate events.";

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <BellIcon className="w-5 h-5" /> Notifications
          </h2>
          <p className="text-sm text-gray-500">VAPT events — scans, findings, remediation, retests, and reports.</p>
          {isPlatformAdmin && selectedCustomerId && (
            <p className="text-xs text-gray-400 mt-1">
              Showing VAPT notifications for:{" "}
              <span className="font-semibold text-gray-600">{selectedCustomerName || selectedCustomerId}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isPlatformAdmin && (
            <select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm min-w-[200px]"
            >
              <option value="">Select a customer…</option>
              {customers.map((c) => (
                <option key={c.id || c.customer_id} value={c.id || c.customer_id}>
                  {c.name || c.company_name || c.email || "Unnamed Customer"}
                </option>
              ))}
            </select>
          )}
          {unreadCount > 0 && (
            <Button variant="secondary" icon={CheckIcon} loading={busy} onClick={handleMarkAllRead}>
              Mark all as read ({unreadCount})
            </Button>
          )}
        </div>
      </div>

      {isPlatformAdmin && !selectedCustomerId && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-lg px-4 py-2.5">
          VAPT notifications are delivered to each customer&apos;s admins and analysts. Select a customer to view theirs.
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5 flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-1 text-xs font-semibold text-red-700 hover:text-red-800 underline"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" /> Retry
          </button>
        </div>
      )}

      {loading ? (
        <PageLoader title="Loading VAPT notifications…" />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.id)}
          emptyMessage={error ? "" : emptyMessage}
          enableColumnVisibility={false}
          enableColumnResizing
          enableColumnFilters
          maxBodyHeight="65vh"
        />
      )}
    </div>
  );
};

export default VaptNotifications;
